// src/encounter/care-plan-readout.tsx
//
// CarePlanReadout · Signal level: block · FHIR: CarePlan (goals: Goal).
//
// The encounter's care-plan READOUT, ported 1:1 from the R6c Encounter Frame (design desk export
// docs/design-desk/exports/R6c/src/"Encounter Frame.dc.html", kind 'careplan', the isCarePlan block),
// under design desk D-14 and D-43. FREEZE-MANIFEST has no CarePlan anchor; per D-43 the R6c export is
// the anchor. DS graduation of this block comes after the 1 Oct pitch (D-43).
//
// Rules held here:
//   · A readout. No verbs, for any persona (D-14). The only control is the expand/collapse row.
//   · Collapsed to one line by default; local state only (D-43 §1).
//   · Nothing while loading or with no plan; ONE line in words on a failed read (D-14, D-43 §6).
//   · The goal verdict is the SERVER's, carried, never recomputed (care-plan-view.ts header). The tally
//     counts the server's 'met' verdicts; it does not compare values to targets.
//   · No wayfinding colour on the block; it inherits the Plan section's (D-43 §7).
//   · The plan line names the patient's ENROLMENT, never this visit's purpose (see `planLine`): the
//     readout shows for every visit of an enrolled patient, whatever the visit was booked for.
//
// The parent decides where it sits (the encounter frame is the chair's, A7). This file does not mount
// itself anywhere.
import { useId, useState } from 'react';
import { cn, StatusChip, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CarePlanView, GoalStatus } from './lib/care-plan-view';
import { latestLine, planLine, tally, targetLine } from './lib/care-plan-readout-text';
import { useCarePlanReadout, type CarePlanReadoutState } from './services/use-care-plans';
import { notCalculatedLine, planNotCalculated, shownGoalStatus, type PlanNotCalculated } from './lib/care-plan-honest';
import { useCareModelRuns } from '@/patients/services/use-care-model-tasks';
import { useAuthStore } from '@/auth/auth-store';
import { isAlliedHealth, isDoctor } from '@/auth/roles';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

const VERDICT: Record<GoalStatus, { semantic: 'success' | 'warning' | 'neutral'; label: string }> = {
  met: { semantic: 'success', label: 'Met' },
  'not-met': { semantic: 'warning', label: 'Not met' },
  'no-data': { semantic: 'neutral', label: 'No result yet' },
  unknown: { semantic: 'neutral', label: 'Status not recognised' },
};
function PlanBlock({
  plan,
  honest = false,
  notCalculated = null,
  noTally = false,
}: {
  plan: CarePlanView;
  honest?: boolean;
  notCalculated?: PlanNotCalculated | null;
  /** `?x=prevent-honest`: the plans' steps (C4) are still loading or failed, so no count can be checked: none is shown. */
  noTally?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const goalsId = useId();
  if (notCalculated) {
    // `?x=prevent-honest` (Samer, 3 Nov): a calculation gave no score, so the plan shows that state and the missing inputs
    // only: no goal tally, no goal verdicts, nothing to expand.
    return (
      <div data-block="CarePlan" data-not-calculated className="flex flex-col gap-1 rounded-xl border border-border-default bg-surface-card px-4 py-3">
        <span className={TEXT_ROLE_CLASSES.body}>{planLine(plan)}</span>
        <span role="status" className={cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary)} data-part="plan-not-calculated">
          {notCalculatedLine(notCalculated)}
        </span>
      </div>
    );
  }
  return (
    <div data-block="CarePlan" className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        // Only while the list exists: a control must not point at an unmounted id.
        aria-controls={open ? goalsId : undefined}
        className="flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-xl border border-border-default bg-surface-card px-4 py-3 text-left hover:bg-surface-background"
      >
        <span className={cn('inline-flex', TEXT_TONE_CLASSES.secondary)}>
          {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        </span>
        <span className={cn('min-w-0 flex-1', TEXT_ROLE_CLASSES.body)}>{planLine(plan)}</span>
        {noTally ? null : (
          <span className={cn('whitespace-nowrap tabular-nums', TEXT_ROLE_CLASSES['meta-strong'], TEXT_TONE_CLASSES.secondary)}>
            {tally(plan, honest)}
          </span>
        )}
      </button>
      {open && (
        <ul id={goalsId} aria-label={`Goals of ${plan.title}`} className="flex flex-col">
          {plan.goals.map((goal) => {
            const verdict = VERDICT[honest ? shownGoalStatus(goal) : goal.status];
            return (
              <li key={goal.id} data-block="Goal" className="flex flex-col gap-1 border-b border-border-subtle px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <span className={cn('min-w-0 flex-1', TEXT_ROLE_CLASSES['body-emphasis'])}>{goal.title}</span>
                  <span className="shrink-0">
                    <StatusChip semantic={verdict.semantic} tone="soft">
                      {verdict.label}
                    </StatusChip>
                  </span>
                </div>
                <span className={cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary)}>{targetLine(goal)}</span>
                <span className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.primary)}>{latestLine(goal)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Presentational: renders exactly what `state` says (D-14). `honest` and `notCalculated`: `?x=prevent-honest`. */
export function CarePlanReadoutView({
  state,
  className,
  honest = false,
  notCalculated,
}: {
  state: CarePlanReadoutState;
  className?: string;
  honest?: boolean;
  notCalculated?: (plan: CarePlanView) => PlanNotCalculated | null;
}) {
  if (state.kind === 'hidden') return null;
  if (state.kind === 'error') {
    return <p className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.secondary, className)}>Care plan couldn&apos;t be loaded.</p>;
  }
  // As the overview's Progress (patient-overview-tab.tsx): with the switch on, while the steps (C4) load, or when that read failed, no
  // plan's step is known, so a plan waiting on a calculation with no score cannot be told apart: no tally is shown.
  const noTally = honest && (state.steps === 'pending' || state.steps === 'failed');
  return (
    <section aria-label="Care plan" className={cn('flex flex-col gap-2', className)}>
      {state.plans.map((plan) => (
        <PlanBlock
          key={plan.carePlanId}
          plan={plan}
          honest={honest}
          notCalculated={honest ? (notCalculated?.(plan) ?? null) : null}
          noTally={noTally}
        />
      ))}
    </section>
  );
}

/** The readout for one patient: C5 plans + C4 current steps. */
export function CarePlanReadout({ patientId, className }: { patientId: string | null | undefined; className?: string }) {
  // `?x=prevent-honest` (P0, Samer 3 Nov; ON on the dev hosts): no on-target claim without a result behind it, and a plan whose
  // calculation gave no score shows only that state and its missing inputs. Read once per mount.
  const [honest] = useState(() => isEncounterFlagOn('preventHonest'));
  // The engine's reason and missing inputs: the run the overview's care-model block reads (same query), physicians only
  // (role 3 also holds allied-health accounts, PROD-2040). Others fall back to the step's own words.
  const physician = useAuthStore((s) => isDoctor(s.user) && !isAlliedHealth(s.user));
  const runs = useCareModelRuns(patientId, honest && physician);
  return (
    <CarePlanReadoutView
      state={useCarePlanReadout(patientId)}
      className={className}
      honest={honest}
      notCalculated={(plan) => planNotCalculated(plan, runs.data)}
    />
  );
}
